import { useEffect } from "react";

import * as Sentry from "@sentry/react";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import { IS_LOCAL_NX_DEV, IS_PROD, SENTRY_IGNORE_ERRORS } from "@bridgesplit/utils";

// Deployment information
const DEPLOYMENT_INFO = {
    version: process.env.NX_DEPLOY_VERSION || "unknown",
    buildTimestamp: process.env.NX_DEPLOY_TIMESTAMP || "unknown"
};

export function initSentry(): void {
    if (IS_LOCAL_NX_DEV) return;

    const environment = IS_PROD ? "production" : "dev";

    Sentry.init({
        dsn: "https://e7a7f7d1dd03ee70de77511eba6499f8@o4504436981497856.ingest.us.sentry.io/4512290023342080",
        integrations: [
            Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: false,
                blockAllMedia: false,
                networkDetailAllowUrls: ["/^(?!auth.loopscale.com).*loopscale/"],
                networkCaptureBodies: true
            }),
            // Router-aware browser tracing supersedes the default browserTracingIntegration.
            // Tighter timeouts (defaults: 1000 / 30000) avoid pegging pageload
            // transactions on the Privy iframe and WebSocket tail.
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
                idleTimeout: 2000,
                finalTimeout: 15000
            })
        ],

        ignoreErrors: SENTRY_IGNORE_ERRORS,
        environment,
        release: `loopscale-app@${DEPLOYMENT_INFO.version}`,
        tracesSampleRate: 0.1,
        tracePropagationTargets: ["/^(?!auth.loopscale.com).*loopscale/"],
        sendDefaultPii: true,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,

        // Set initial scope with deployment tags
        initialScope: {
            tags: {
                app_version: DEPLOYMENT_INFO.version,
                build_timestamp: DEPLOYMENT_INFO.buildTimestamp
            }
        },

        beforeSend(event, hint) {
            // Add deployment context to every error
            event.tags = {
                ...event.tags,
                app_version: DEPLOYMENT_INFO.version,
                build_timestamp: DEPLOYMENT_INFO.buildTimestamp
            };

            event.contexts = {
                ...event.contexts,
                deployment: {
                    version: DEPLOYMENT_INFO.version,
                    buildTimestamp: DEPLOYMENT_INFO.buildTimestamp
                }
            };

            return event;
        }
    });

    // Store deployment info in session storage for support
    if (typeof window !== "undefined") {
        const sessionInfo = {
            ...DEPLOYMENT_INFO,
            sessionId: crypto.randomUUID(),
            loadedAt: new Date().toISOString()
        };
        sessionStorage.setItem("deployment_info", JSON.stringify(sessionInfo));
        (window as any).__DEPLOYMENT_INFO__ = sessionInfo;
    }
}
